import { useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Upload, X } from "lucide-react";
import { submitLead, uploadLeadFiles } from "@/lib/api";

const FACILITY_TYPES = [
  { value: "golf-course", label: "Golf Course" },
  { value: "driving-range", label: "Driving Range" },
  { value: "sports-facility", label: "Sports Facility" },
  { value: "commercial-outdoor", label: "Commercial Outdoor" },
  { value: "other", label: "Other" },
];
const EXISTING = [
  { value: "none", label: "No existing lighting" },
  { value: "partial", label: "Partial / aging system" },
  { value: "full-upgrade", label: "Full system, needs upgrade" },
];
const BUDGETS = [
  { value: "under-25k", label: "Under $25k" },
  { value: "25k-75k", label: "$25k – $75k" },
  { value: "75k-150k", label: "$75k – $150k" },
  { value: "150k-500k", label: "$150k – $500k" },
  { value: "over-500k", label: "Over $500k" },
  { value: "not-sure", label: "Not sure yet" },
];
const TIMELINES = [
  { value: "asap", label: "As soon as possible" },
  { value: "1-3-months", label: "1–3 months" },
  { value: "3-6-months", label: "3–6 months" },
  { value: "6-12-months", label: "6–12 months" },
  { value: "exploring", label: "Just exploring" },
];

const STEPS = ["Project", "Scope & Budget", "Contact", "Review"];

const inputCls =
  "w-full bg-white/[0.04] border hairline rounded-sm px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#A7D7A0]/60 transition-colors";
const labelCls = "block text-xs font-mono-tech uppercase tracking-[0.2em] text-white/50 mb-2";

const Chip = ({ selected, onClick, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`px-4 py-2.5 rounded-sm border text-sm transition-all duration-300 ${
      selected
        ? "border-[#A7D7A0] bg-[#A7D7A0]/10 text-white"
        : "hairline text-white/60 hover:text-white hover:border-white/25"
    }`}
  >
    {children}
  </button>
);

export default function QuoteForm() {
  const location = useLocation();
  const calc = location.state?.calculator || null;

  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [files, setFiles] = useState([]);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    full_name: "",
    company: "",
    email: "",
    phone: "",
    location: "",
    facility_type: "golf-course",
    holes_or_area: calc?.holes ? `${calc.holes} holes` : "",
    existing_lighting: "none",
    budget: "not-sure",
    timeline: "exploring",
    description: calc
      ? `Calculator estimate context: ${calc.holes} holes, ~${calc.rounds_per_night} additional rounds/night, $${calc.revenue_per_round}/round, ${calc.nights_per_month} nights/month.`
      : "",
    website: "",
  });

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validateStep = (s) => {
    const e = {};
    if (s === 0) {
      if (!form.holes_or_area.trim()) e.holes_or_area = "Tell us the number of holes or approximate area.";
    }
    if (s === 2) {
      if (form.full_name.trim().length < 2) e.full_name = "Full name is required.";
      if (form.company.trim().length < 2) e.company = "Company or course name is required.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "A valid business email is required.";
      if (!/^[0-9+\-()\s.]{7,30}$/.test(form.phone)) e.phone = "A valid phone number is required.";
      if (form.location.trim().length < 2) e.location = "Project location is required.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validateStep(step)) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const onPickFiles = (list) => {
    const arr = Array.from(list).slice(0, 3);
    const bad = arr.find((f) => f.size > 5 * 1024 * 1024);
    if (bad) {
      toast.error("Each file must be under 5MB.");
      return;
    }
    setFiles(arr);
  };

  const submit = async () => {
    if (!validateStep(2)) {
      setStep(2);
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, calculator: calc || undefined };
      const res = await submitLead(payload);
      if (files.length) {
        try {
          await uploadLeadFiles(res.lead_id, files);
        } catch {
          toast.error("Brief saved, but file upload failed — you can email photos to us directly.");
        }
      }
      setDone(res);
      window.scrollTo({ top: 0 });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please review the form and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const summary = useMemo(
    () => [
      ["Facility", FACILITY_TYPES.find((f) => f.value === form.facility_type)?.label],
      ["Holes / Area", form.holes_or_area],
      ["Existing Lighting", EXISTING.find((f) => f.value === form.existing_lighting)?.label],
      ["Budget", BUDGETS.find((f) => f.value === form.budget)?.label],
      ["Timeline", TIMELINES.find((f) => f.value === form.timeline)?.label],
      ["Contact", `${form.full_name} — ${form.company}`],
      ["Email / Phone", `${form.email} · ${form.phone}`],
      ["Location", form.location],
    ],
    [form]
  );

  if (done) {
    return (
      <div className="text-center py-16" data-testid="quote-confirmation">
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#326B46]/20 border border-[#326B46] mb-8">
          <Check size={28} className="text-[#A7D7A0]" />
        </span>
        <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">Brief Received.</h3>
        <p className="mt-5 text-muted-green max-w-md mx-auto leading-relaxed text-sm sm:text-base">{done.message}</p>
        <p className="mt-6 font-mono-tech text-xs text-white/40 tracking-[0.2em]">
          REFERENCE: <span data-testid="quote-lead-id">{done.lead_id}</span>
        </p>
        <p className="mt-2 text-xs text-white/30">A confirmation email will follow once email delivery is configured.</p>
      </div>
    );
  }

  return (
    <div data-testid="quote-form">
      {/* Progress */}
      <div className="flex items-center gap-2 mb-10">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1">
            <div className={`h-0.5 rounded-full transition-colors duration-500 ${i <= step ? "bg-[#A7D7A0]" : "bg-white/10"}`} />
            <span className={`block mt-2 text-[10px] font-mono-tech uppercase tracking-[0.2em] ${i <= step ? "text-white" : "text-white/30"}`}>
              {i + 1}. {s}
            </span>
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="space-y-8">
          <div>
            <span className={labelCls}>Facility Type</span>
            <div className="flex flex-wrap gap-2.5">
              {FACILITY_TYPES.map((f) => (
                <Chip key={f.value} testid={`quote-facility-${f.value}`} selected={form.facility_type === f.value} onClick={() => set("facility_type", f.value)}>
                  {f.label}
                </Chip>
              ))}
            </div>
          </div>
          <div>
            <label className={labelCls} htmlFor="quote-area">Number of Holes / Approximate Area</label>
            <input id="quote-area" data-testid="quote-area-input" className={inputCls} placeholder="e.g. 18 holes, or ~12 acres" value={form.holes_or_area} onChange={(e) => set("holes_or_area", e.target.value)} />
            {errors.holes_or_area && <p className="mt-2 text-xs text-red-400" data-testid="quote-area-error">{errors.holes_or_area}</p>}
          </div>
          <div>
            <span className={labelCls}>Existing Lighting Infrastructure</span>
            <div className="flex flex-wrap gap-2.5">
              {EXISTING.map((f) => (
                <Chip key={f.value} testid={`quote-existing-${f.value}`} selected={form.existing_lighting === f.value} onClick={() => set("existing_lighting", f.value)}>
                  {f.label}
                </Chip>
              ))}
            </div>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-8">
          <div>
            <span className={labelCls}>Estimated Project Budget</span>
            <div className="flex flex-wrap gap-2.5">
              {BUDGETS.map((f) => (
                <Chip key={f.value} testid={`quote-budget-${f.value}`} selected={form.budget === f.value} onClick={() => set("budget", f.value)}>
                  {f.label}
                </Chip>
              ))}
            </div>
          </div>
          <div>
            <span className={labelCls}>Desired Installation Timeline</span>
            <div className="flex flex-wrap gap-2.5">
              {TIMELINES.map((f) => (
                <Chip key={f.value} testid={`quote-timeline-${f.value}`} selected={form.timeline === f.value} onClick={() => set("timeline", f.value)}>
                  {f.label}
                </Chip>
              ))}
            </div>
          </div>
          <div>
            <label className={labelCls} htmlFor="quote-description">Project Description</label>
            <textarea id="quote-description" data-testid="quote-description-input" rows={5} className={inputCls} placeholder="Tell us about your facility, goals, and anything else we should know." value={form.description} onChange={(e) => set("description", e.target.value)} />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className={labelCls} htmlFor="quote-name">Full Name</label>
            <input id="quote-name" data-testid="quote-name-input" className={inputCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
            {errors.full_name && <p className="mt-2 text-xs text-red-400">{errors.full_name}</p>}
          </div>
          <div>
            <label className={labelCls} htmlFor="quote-company">Company / Golf Course Name</label>
            <input id="quote-company" data-testid="quote-company-input" className={inputCls} value={form.company} onChange={(e) => set("company", e.target.value)} />
            {errors.company && <p className="mt-2 text-xs text-red-400">{errors.company}</p>}
          </div>
          <div>
            <label className={labelCls} htmlFor="quote-email">Business Email</label>
            <input id="quote-email" data-testid="quote-email-input" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} />
            {errors.email && <p className="mt-2 text-xs text-red-400">{errors.email}</p>}
          </div>
          <div>
            <label className={labelCls} htmlFor="quote-phone">Phone Number</label>
            <input id="quote-phone" data-testid="quote-phone-input" type="tel" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            {errors.phone && <p className="mt-2 text-xs text-red-400">{errors.phone}</p>}
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="quote-location">Project Address or Location</label>
            <input id="quote-location" data-testid="quote-location-input" className={inputCls} placeholder="City, State — or full address" value={form.location} onChange={(e) => set("location", e.target.value)} />
            {errors.location && <p className="mt-2 text-xs text-red-400">{errors.location}</p>}
          </div>
          {/* Honeypot — invisible to humans */}
          <input type="text" name="website" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
        </div>
      )}

      {step === 3 && (
        <div className="space-y-8">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
            {summary.map(([k, v]) => (
              <div key={k} className="border-b hairline pb-3">
                <dt className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-white/40">{k}</dt>
                <dd className="mt-1 text-sm text-white/90">{v || "—"}</dd>
              </div>
            ))}
          </dl>

          <div>
            <span className={labelCls}>Site Photos or Drawings (optional, up to 3 · 5MB each)</span>
            <button
              type="button"
              data-testid="quote-upload-btn"
              onClick={() => fileRef.current?.click()}
              className="w-full border border-dashed border-white/20 rounded-sm py-8 flex flex-col items-center gap-2 text-white/50 hover:border-[#A7D7A0]/50 hover:text-white/80 transition-colors"
            >
              <Upload size={20} />
              <span className="text-sm">Click to attach photos, plans, or drawings</span>
            </button>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" multiple className="hidden" data-testid="quote-file-input" onChange={(e) => onPickFiles(e.target.files)} />
            {files.length > 0 && (
              <ul className="mt-4 space-y-2">
                {files.map((f, i) => (
                  <li key={f.name} className="flex items-center justify-between text-sm text-white/70 bg-white/[0.03] rounded-sm px-4 py-2.5">
                    <span className="truncate">{f.name}</span>
                    <button type="button" data-testid={`quote-file-remove-${i}`} onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-white/40 hover:text-red-400 ml-4">
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <div className="mt-10 flex items-center justify-between">
        <button
          type="button"
          data-testid="quote-back-btn"
          onClick={() => setStep((s) => Math.max(s - 1, 0))}
          disabled={step === 0}
          className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white disabled:opacity-0 transition-all"
        >
          <ArrowLeft size={15} /> Back
        </button>
        {step < STEPS.length - 1 ? (
          <button
            type="button"
            data-testid="quote-next-btn"
            onClick={next}
            className="inline-flex items-center gap-2 bg-white text-[#101214] font-semibold text-sm px-7 py-3 rounded-sm hover:bg-[#A7D7A0] transition-colors duration-300"
          >
            Continue <ArrowRight size={15} />
          </button>
        ) : (
          <button
            type="button"
            data-testid="quote-submit-btn"
            onClick={submit}
            disabled={submitting}
            className="inline-flex items-center gap-2 bg-[#A7D7A0] text-[#101214] font-semibold text-sm px-7 py-3 rounded-sm hover:bg-white transition-colors duration-300 disabled:opacity-50"
          >
            {submitting ? "Submitting…" : "Submit Project Brief"} <ArrowRight size={15} />
          </button>
        )}
      </div>
    </div>
  );
}
